import getSymbolFromCurrency from "currency-symbol-map";
import { v4 } from "uuid";

import {
  AnyProgressBarWidgetStyles,
  AnyProgressBarWidgetVariables,
  AudienceFiltersDto,
  ConditionType,
  CreateAudienceDto,
  CreateExclusionGroupEntityFe,
  CreateExclusionGroupFe,
  CreateExperienceAnalysis,
  DiscountApplicationType,
  DiscountQuantityWidgetStyles,
  DiscountTierDto,
  ExperienceCategory,
  ExperiencePageTargetingFe,
  ExperienceType,
  MessageBoxWidgetStyles,
  MessageBoxWidgetVariables,
  OfferDto,
  OfferQuantityButtonType,
  OnboardingStateType,
  RateConditionType,
  RateGroupType,
  RateType,
  RedirectDto,
  RedirectFilter,
  ShippingRateFe,
  ShippingRateGroupFe,
  ShopifyCurrencyType,
  ShopifyTemplateTypeType,
  WidgetDto,
  WidgetTypeType,
  WidgetVariant,
} from "@intelligems/ig-types";

import theme from "../theme";
import { ExperienceFe } from "../types/schemas/experiences";
import { VariationFe, VariationFeProps } from "../types/schemas/variations";
import { getRounded } from "../utils/variations";

import { calculatePriceDirection } from "./formatters/products";

export const DISCOUNT_MAX_UNITS = 1000000000;
export const excludeDupProducts = "-price_test AND ";

export const getDefaultExperienceName = (
  hasTestPricing: boolean,
  hasTestShipping: boolean,
  hasTestCampaign: boolean,
  title?: string
): string => {
  const pricing = "Pricing";
  const shipping = "Shipping";
  const campaign = "Campaign";
  const content = "Content";

  if (title) {
    if (!title.includes(pricing) && hasTestPricing) {
      return getDefaultExperienceName(
        hasTestPricing,
        hasTestShipping,
        hasTestCampaign,
        `${title} + ${pricing}`
      );
    } else if (!title.includes(shipping) && hasTestShipping) {
      return getDefaultExperienceName(
        hasTestPricing,
        hasTestShipping,
        hasTestCampaign,
        `${title} + ${shipping}`
      );
    } else if (!title.includes(campaign) && hasTestCampaign) {
      return getDefaultExperienceName(
        hasTestPricing,
        hasTestShipping,
        hasTestCampaign,
        `${title} + ${campaign}`
      );
    } else {
      return `${title} Test`;
    }
  } else {
    if (hasTestPricing) {
      return getDefaultExperienceName(
        hasTestPricing,
        hasTestShipping,
        hasTestCampaign,
        pricing
      );
    } else if (hasTestShipping) {
      return getDefaultExperienceName(
        hasTestPricing,
        hasTestShipping,
        hasTestCampaign,
        shipping
      );
    } else if (hasTestCampaign) {
      return getDefaultExperienceName(
        hasTestPricing,
        hasTestShipping,
        hasTestCampaign,
        campaign
      );
    } else {
      return getDefaultExperienceName(
        hasTestPricing,
        hasTestShipping,
        hasTestCampaign,
        content
      );
    }
  }
};

export const initialFrontendProps = (options: {
  name: string;
  percentage: number;
  order: number;
  priceChange: string | number | null | undefined;
  count: number;
  useWideApp: boolean;
}): VariationFeProps => {
  return {
    label: options.name,
    backgroundColor: theme.palette.primary.main,
    width: options.percentage * 10 * (options.useWideApp ? 2 : 1),
    priceDirection: calculatePriceDirection(options.priceChange),
    rounded: getRounded(options.order, options.count),
    priceRoundingType: "none",
    priceCustomRoundingAmount: 0.0,
  };
};

export const initialAudienceState = (
  priceTestDefaultCurrency?: string
): CreateAudienceDto => {
  return {
    id: v4(),
    enabled: true,
    excludeCurrency: {
      exclude: !!priceTestDefaultCurrency,
      currency: priceTestDefaultCurrency || null,
      country: undefined,
      region: undefined,
      city: undefined,
    },
    wholesale: false,
    audienceType: "common",
    elseAction: null,
    elseVariationId: null,
    elseExcludeFromAnalytics: false,
    filters: [],
  };
};

export const initialAudienceFilterState = (): AudienceFiltersDto => {
  return {
    id: v4(),
    priority: 0,
    filterType: "utm",
    action: null,
    excludeFromAnalytics: false,
    expressionType: "common",
    expression: [
      {
        query: {
          key: "",
          value: "",
          filter: "equals",
          type: "utm",
        },
      },
    ],
  };
};
export const initialPageTargetingState = (
  experienceId?: string,
  domain?: string
): ExperiencePageTargetingFe => {
  return {
    id: v4(),
    filter: "equals",
    value: domain ? domain : "",
    order: 0,
    filterType: "url",
    experienceId,
  };
};
export const initialRedirectState = (
  variation: VariationFe,
  group: number,
  isThemeTest = false,
  templateType?: ShopifyTemplateTypeType | null,
  isTemplateTest?: boolean,
  copyRedirect?: RedirectDto,
  redirectFilter?: RedirectFilter,
  skip?: boolean
): RedirectDto => {
  return {
    id: v4(),
    group,
    originUrl: copyRedirect?.originUrl ?? "",
    queryParams: [],
    destinationUrl: copyRedirect?.destinationUrl ?? "",
    skip: skip ?? (variation.isControl && !isTemplateTest && !isThemeTest),
    variationId: variation.id,
    isThemeTest,
    isTemplateTest:
      isTemplateTest !== undefined ? isTemplateTest : !!templateType,
    templateType: templateType,
    templateSuffixes: [],
    redirectOnce: true,
    filter: copyRedirect?.filter ?? redirectFilter ?? "none",
    find: undefined,
    redirectType: copyRedirect?.redirectType ?? "fullReplacement",
  };
};

export const initialDiscountTierState = (): DiscountTierDto[] => {
  return [
    {
      id: v4(),
      minimumUnits: 0,
      maximumUnits: DISCOUNT_MAX_UNITS,
      unitDiscount: 0,
      discountTitle: null,
      isFreeShipping: false,
      isGiftWithPurchase: false,
      autoAddGiftWithPurchase: true,
    },
  ];
};

export const initialExperienceAnalysis = (options: {
  testFlowType: ExperienceType;
  useWideApp: boolean;
  defaultCurrency?: string;
  hasTestPricing: boolean;
  hasTestShipping: boolean;
  hasTestCampaign: boolean;
  hasTestContent: boolean;
  hasTestContentUrl: boolean;
  hasTestContentAdvanced: boolean;
  hasTestContentOnsite: boolean;
  hasTestContentTemplate: boolean;
  hasTestContentTheme: boolean;
  hasTestOnsiteInjections: boolean;
}): CreateExperienceAnalysis => {
  return {
    chosenWinnerVariationId: null,
    impact: null,
    orders: null,
    impactWinnerVariationId: null,
    excludeFromImpact: false,
    analysisUrl: "",
    estimatedTestLengthRange: { high: null, low: null },
    visitors: null,
    analyticsViewType: options.hasTestPricing
      ? "none"
      : options.hasTestShipping ||
          options.hasTestCampaign ||
          (!options.hasTestCampaign &&
            !options.hasTestShipping &&
            !options.hasTestPricing)
        ? "sitewide_orders"
        : "none",
    goal: null,
  };
};

// this is for experiment
export const initialExperienceFeDto = (options: {
  testFlowType: ExperienceType;
  useWideApp: boolean;
  defaultCurrency?: string;
  hasTestPricing: boolean;
  hasTestShipping: boolean;
  hasTestCampaign: boolean;
  hasTestContent: boolean;
  hasTestContentUrl: boolean;
  hasTestContentAdvanced: boolean;
  hasTestContentOnsite: boolean;
  hasTestContentTemplate: boolean;
  hasTestContentTheme: boolean;
  hasTestOnsiteInjections: boolean;
  category?: ExperienceCategory;
}): ExperienceFe => {
  return {
    id: undefined,
    isPreview: true,
    category: options.category ?? "experiment",
    viewMode: "edit",
    actionMode: "create",
    step: 0,
    productsLocation: null,
    name: "",
    description: "",
    status: "pending",
    experienceActions: [],
    isExternal: false,
    testTypes: {
      hasTestPricing: options.hasTestPricing,
      hasTestShipping: options.hasTestShipping,
      hasTestCampaign: options.hasTestCampaign,
      hasTestContent: options.hasTestContent,
      hasTestContentUrl: options.hasTestContentUrl,
      hasTestContentAdvanced: options.hasTestContentAdvanced,
      hasTestContentOnsite: options.hasTestContentOnsite,
      hasTestContentTemplate: options.hasTestContentTemplate,
      hasTestContentTheme: options.hasTestContentTheme,
      hasTestOnsiteInjections: options.hasTestOnsiteInjections,
    },
    experienceIntegrations: [],
    experienceAnalysis: initialExperienceAnalysis(options),
    requiresLink: false,
    linkBaseUrl: null,
    devicePreview: {
      device: "desktop",
      path: "/",
    },
    shippingTestMethodDefinitions: [],
    type: options.testFlowType,
    startedAtTs: null,
    endedAtTs: null,
    pausedAtTs: null,
    productVariantCount: 0,
    currency:
      options.hasTestPricing && options.defaultCurrency
        ? {
            code: options.defaultCurrency,
            symbol: getSymbolFromCurrency(options.defaultCurrency) || "$",
          }
        : null,
  };
};

export const initialOfferQuantityButtonsState =
  (): OfferQuantityButtonType[] => {
    return [
      { id: v4(), quantity: 1, unitName: "pack" },
      { id: v4(), quantity: 2, unitName: "packs" },
      {
        id: v4(),
        quantity: 3,
        unitName: "packs",
        extraMessage: "Most Popular",
      },
    ];
  };

export const initialDiscountProgressBarVariables =
  (): AnyProgressBarWidgetVariables => {
    return {
      showLabelsBelowBreakpoints: false,
      textReplacements: {
        unitName: undefined,
        more: undefined,
        buyMoreTemplate: undefined,
        allTiersUnlockedTemplate: undefined,
        currentUnlockedTemplate: undefined,
        tooltipTemplate: undefined,
        tierVariables: {},
      },
    };
  };

export const initialDiscountMessageBoxVariables =
  (): MessageBoxWidgetVariables => {
    return {
      textReplacements: {
        body: "",
      },
    };
  };

export const initialOfferState = ({
  totalCount,
  isTest,
  variationId,
  discountApplicationType,
}: {
  totalCount?: number;
  isTest?: boolean;
  variationId: string;
  discountApplicationType?: DiscountApplicationType;
}): OfferDto => {
  return {
    id: v4(),
    enabled: false,
    isTest: isTest || false,
    name: totalCount ? `Discount ${totalCount + 1}` : `Discount`,
    unitType: "unit",
    discountType: "percentage",
    discountApplicationType: discountApplicationType || "",
    discountApplicationMethod: "perItem",
    tiers: initialDiscountTierState(),
    isArchived: false,
    shouldStack: false,
    shouldRejectCodes: false,
    discountApp: null,
    variationId,
  };
};

export const initialTierShippingState = (
  tierType: RateConditionType,
  unitType: ConditionType
) => {
  return {
    min: null,
    max: null,
    conditionType: tierType,
    unit: unitType,
  };
};

export const initialShippingRateState = (
  storeCurrency: ShopifyCurrencyType,
  rateType: RateType,
  index: number,
  selectedRateType: RateGroupType,
  tierType: RateConditionType,
  unitType: ConditionType
): ShippingRateFe => {
  return {
    name: "",
    amount: null,
    rateType: rateType,
    currency: storeCurrency,
    condition:
      selectedRateType === "tieredByPriceOrWeight"
        ? initialTierShippingState(tierType, unitType)
        : undefined,
  };
};

export const initialShippingRateGroupsState = (
  selectedRateType: RateGroupType,
  storeCurrency: ShopifyCurrencyType,
  tierType: RateConditionType,
  unitType: ConditionType,
  rateGroupPosition: number,
  variationId: string
): ShippingRateGroupFe => {
  const types: RateType[] = [];

  if (selectedRateType === "flatRate") {
    types.push("amount");
  } else if (selectedRateType === "flatRateWithThreshold") {
    types.push("amount", "threshold");
  } else if (selectedRateType === "thresholdOnly") {
    types.push("threshold");
  } else {
    types.push("tier");
  }
  return {
    groupType: selectedRateType,
    position: rateGroupPosition,
    variationId,
    rates:
      selectedRateType === "custom"
        ? []
        : types.map((rate, index) =>
            initialShippingRateState(
              storeCurrency,
              rate,
              index,
              selectedRateType,
              tierType,
              unitType
            )
          ),
  };
};

export const initialSingleVariationState = (
  useWideApp: boolean
): VariationFe => {
  return {
    id: v4(),
    isControl: true,
    name: "Control Group",
    order: 0,
    percentage: 100,
    priceChange: undefined,
    priceChangeUnit: "percent",
    shippingRateGroups: [],
    excludeProducts: false,
    includeProducts: false,
    functionSyncError: false,
    ...initialFrontendProps({
      name: "Control Group",
      percentage: 100,
      order: 0,
      priceChange: undefined,
      count: 1,
      useWideApp,
    }),
  };
};

export const initialVariationState = (useWideApp: boolean): VariationFe[] => {
  return [
    {
      id: v4(),
      isControl: true,
      name: "Control Group",
      order: 0,
      percentage: 50,
      priceChange: undefined,
      priceChangeUnit: "percent",
      shippingRateGroups: [],
      excludeProducts: false,
      includeProducts: false,
      functionSyncError: false,
      ...initialFrontendProps({
        name: "Control Group",
        percentage: 50,
        order: 0,
        priceChange: undefined,
        count: 1,
        useWideApp,
      }),
    },
    {
      id: v4(),
      isControl: false,
      name: "New Group 1",
      order: 1,
      percentage: 50,
      priceChange: undefined,
      functionSyncError: false,
      priceChangeUnit: "percent",
      excludeProducts: false,
      includeProducts: false,
      shippingRateGroups: [],
      ...initialFrontendProps({
        name: "New Group 1",
        percentage: 50,
        order: 1,
        priceChange: undefined,
        count: 2,
        useWideApp,
      }),
    },
  ];
};

export const initialNewVariationState = (
  maxOrder: number,
  testGroupLength: number
): VariationFe => {
  return {
    id: v4(),
    name: `New Group ${maxOrder + 1}`,
    isControl: false,
    order: maxOrder + 1,
    percentage: 100 / (testGroupLength + 1),
    priceChange: undefined,
    priceChangeUnit: "percent",
    shippingRateGroups: [],
    label: `New Group ${maxOrder + 1}`,
    backgroundColor: theme.palette.primary.main,
    rounded: "right",
    priceDirection: "increase",
    width: 1000 / (testGroupLength + 1),
    priceRoundingType: "none",
    priceCustomRoundingAmount: 0.0,
    excludeProducts: false,
    includeProducts: false,
    functionSyncError: false,
  };
};

export const initialOnboardingState: OnboardingStateType = {
  activeStep: 0,
  selectedPlan: undefined,
  isComplete: false,
  progress: 0,
};

export const initializeWidget = (
  widgetType: WidgetTypeType,
  variant: WidgetVariant,
  parentId?: string
): WidgetDto => {
  let name;
  if (widgetType === "quantityButtons") {
    name = "New Button";
  } else if (widgetType === "discountProgressBar") {
    name = "New Offer Progress Bar";
  } else if (widgetType === "messageBoxPopup") {
    name = "New Pop Up";
  } else if (widgetType === "messageBoxSlideOut") {
    name = "New Slide Out";
  } else if (widgetType === "shippingProgressBar") {
    name = "New Shipping Progress Bar";
  } else {
    name = "New Widget";
  }
  let variables = {};
  let styles = {};
  if (widgetType === "quantityButtons") {
    styles = {
      colors: {
        border: "#BBB",
        primary: "#F1F1F1",
        secondary: "#E0E0E0",
      },
    } as DiscountQuantityWidgetStyles;
  } else if (
    widgetType === "discountProgressBar" ||
    widgetType === "shippingProgressBar"
  ) {
    styles = {
      bar: {
        activeColor: "#E0E0E0",
        inactiveColor: "#F1F1F1",
        backgroundColor: "#ffffff",
      },
      container: { backgroundColor: "#ffffff" },
      breakpoint: {
        activeColor: "#E0E0E0",
        inactiveColor: "#F1F1F1",
      },
    } as AnyProgressBarWidgetStyles;
  } else if (widgetType === "messageBoxPopup") {
    styles = {
      container: {
        backgroundColor: "#F1F1F1",
        textColor: "#494C50",
      },
    } as MessageBoxWidgetStyles;
  } else if (widgetType === "messageBoxSlideOut") {
    styles = {
      container: {
        backgroundColor: "#F1F1F1",
        textColor: "#494C50",
      },
    } as MessageBoxWidgetStyles;
  }

  if (widgetType === "shippingProgressBar") {
    variables = {
      textReplacements: {
        buyMoreTemplate: "Almost there! Add #quantity to unlock Free Shipping!",
      },
    } as AnyProgressBarWidgetVariables;
  }

  return {
    id: v4(),
    name,
    enabledSitewide: false,
    widgetType: widgetType,
    config: {
      desktop: {
        variant: variant as any,
        styles,
        variables,
      },
    },
    parentId,
  };
};

export const initialExclusionGroupEntityStateFunc = (
  entityId?: string,
  order?: number
): CreateExclusionGroupEntityFe => {
  return {
    percentage: 0,
    order: order ?? 0,
    hasPriorityOrder: false,
    experienceId: entityId,
  };
};

export const initialExclusionGroupStateFunc = (
  entityId?: string
): CreateExclusionGroupFe => {
  return {
    name: "",
    exclusionGroupEntities: [initialExclusionGroupEntityStateFunc(entityId)],
    reassignOnEnd: true,
    hasWeightedValues: false,
  };
};
